import chalk from "chalk";
import { PublicKey } from "@solana/web3.js";
import { getCrunchesForCoordinatorWallet } from "@crunchdao/sdk";
import { log, formatMicroUsdcToUsdc } from "@crunchdao/sdk";
import { formatAddressWithLink } from "@crunchdao/shared-cli";
import { createTable } from "@crunchdao/shared-cli";
import { BaseCommand } from "@crunchdao/shared-cli";

export class ListCrunchesCommand extends BaseCommand {
  private formatStatus(status: string): string {
    switch (status.toLowerCase()) {
      case "created":
        return chalk.blue("🔵 Created");
      case "started":
        return chalk.green("🟢 Started");
      case "ended":
        return chalk.red("🔴 Ended");
      default:
        return chalk.white(`⚪ ${status}`);
    }
  }

  private displayCrunchesInfo(crunches: any[], coordinatorAddress: string) {
    // Header with coordinator info
    console.log(chalk.cyan.bold(`\nCrunches for Coordinator: ${coordinatorAddress}`));
    console.log(chalk.gray("═".repeat(80)));

    if (crunches.length === 0) {
      console.log(chalk.yellow("No crunches found for this coordinator"));
      return;
    }

    // Main crunches table
    const crunchesTable = createTable({ colWidths: [5, 20, 15, 15, 10, 20] });
    crunchesTable.push([
      chalk.yellow("#"),
      chalk.yellow("Name"),
      chalk.yellow("Status"),
      chalk.yellow("Payout"),
      chalk.yellow("Max Models"),
      chalk.yellow("Coordinator"),
    ]);

    crunches.forEach((crunch, index) => {
      crunchesTable.push([
        chalk.white((index + 1).toString()),
        chalk.cyan(crunch.name),
        this.formatStatus(crunch.state),
        chalk.green(`${crunch.payoutAmount} USDC`),
        chalk.white(crunch.maxModels.toString()),
        formatAddressWithLink(crunch.coordinatorAddress, this.network),
      ]);
    });

    console.log(crunchesTable.toString());
  }

  async execute(ownerWallet?: string): Promise<void> {
    try {
      const walletAddress = ownerWallet
        ? new PublicKey(ownerWallet)
        : this.provider.wallet.publicKey;
      log.info(`Fetching crunches for coordinator wallet: ${walletAddress.toBase58()}`);

      const crunches = await getCrunchesForCoordinatorWallet(this.program, walletAddress);

      const crunchList = crunches.map((crunch) => ({
        name: crunch.name,
        state: Object.keys(crunch.state)[0],
        payoutAmount: formatMicroUsdcToUsdc(crunch.payoutAmount.toNumber(), 6, false),
        maxModels: crunch.maxModelsPerCruncher,
        coordinatorAddress: crunch.coordinator.toBase58(),
        // Enhanced data for JSON output
        coordinator: formatAddressWithLink(crunch.coordinator.toBase58(), this.network),
      }));

      const enhancedOutput = {
        coordinatorAddress: walletAddress.toBase58(),
        totalCrunches: crunchList.length,
        crunches: crunchList,
      };

      // Output based on format preference
      if (this.resolvedOptions.output === "json") {
        this.output(enhancedOutput);
      } else {
        // Create beautiful table output
        if (crunchList.length === 0) {
          console.log(chalk.yellow("\nNo crunches found for this coordinator"));
          console.log(chalk.gray(`Coordinator: ${walletAddress.toBase58()}`));
        } else {
          log.info(
            `Found ${crunchList.length} crunch(es) for coordinator: ${walletAddress.toBase58()}`
          );
          this.displayCrunchesInfo(crunchList, walletAddress.toBase58());
        }
      }
    } catch (error) {
      log.error("Error fetching crunches:", error);
      this.handleError(error);
    }
  }
}
